import { omitBy } from 'lodash'

import { type DataAttributes } from './types'

const DATA_ATTRIBUTE_PREFIX = 'data-'

const isDataAttributeKey = (key: string): key is `data-${string}` =>
  key.startsWith(DATA_ATTRIBUTE_PREFIX)

const isDataAttribute = (_value: unknown, key: string) =>
  isDataAttributeKey(key)

// Hand-rolled rather than lodash `pickBy`, which types its result as
// `Partial<TProps>` — a rendered element rejects a spread that wide.
const pickDataAttributes = (props: object): DataAttributes => {
  const dataAttributes: DataAttributes = {}

  Object.entries(props).forEach(([key, value]) => {
    if (isDataAttributeKey(key)) {
      dataAttributes[key] = value
    }
  })

  return dataAttributes
}

/**
 * Partition a props bag into its `data-*` attributes and everything else.
 *
 * For a component whose rest props are forwarded to an inner component rather
 * than the element it renders: `data-*` belongs on the rendered root, since that
 * is the only element a consumer can query and match against from the outside,
 * while the remaining props belong to the inner component.
 */
export const splitDataAttributes = <TProps extends object>(props: TProps) => ({
  dataAttributes: pickDataAttributes(props),
  otherProps: omitBy(props, isDataAttribute),
})
